import type { SubstitutePlacement } from '@/utils/substituteLayout';
import type { FC } from 'react';

interface SubstituteTextProps {
  text: string;
  placement: SubstitutePlacement;
  /** Colour of the glyphs the substitute replaces. */
  ink: string;
  /** Pixels per PDF point at the current render size. */
  factor: number;
  /** Where the box holding this text starts, in CSS pixels on the page, to draw relative to it. */
  boxLeft: number;
  boxTop: number;
}

/**
 * One substitute (or one line's part of it), on the baseline of the text it replaces.
 *
 * SVG rather than an HTML span because SVG text is positioned by its baseline, which is the point
 * the export places a substitute at; a span would need the font's ascent to get there. The text is
 * never clipped or ellipsized: `placement` has already condensed it to the room it replaces at the
 * original size (`fitSubstitute`), in the font the export uses or its closest installed twin
 * (`utils/substituteFont`).
 */
export const SubstituteText: FC<SubstituteTextProps> = ({ text, placement, ink, factor, boxLeft, boxTop }) => {
  const x = placement.x * factor - boxLeft;
  const y = placement.baseline * factor - boxTop;
  const scale = placement.horizontalScale;
  return (
    // `z-10` lifts the text above the covers of the whole page, not only its own: a cover reaches
    // from ascent to descent, so the one of the next line down would clip this text's descenders.
    <svg aria-hidden="true" className="pointer-events-none absolute left-0 top-0 z-10 h-full w-full overflow-visible">
      <text
        x={x}
        y={y}
        fill={ink}
        // A condensed run (`Tz`) is condensed around the start of its own baseline.
        transform={scale === 1 ? undefined : `translate(${x} ${y}) scale(${scale} 1) translate(${-x} ${-y})`}
        style={{
          fontFamily: placement.font.family,
          fontWeight: placement.font.weight,
          fontStyle: placement.font.style,
          fontSize: `${placement.fontSize * factor}px`,
          fontKerning: 'none',
          whiteSpace: 'pre',
        }}
      >
        {text}
      </text>
    </svg>
  );
};
